import {
    isMathExpression,
    evaluateMathExpression,
    formatPriceMathResult,
    sanitizePriceMathInput,
} from '../helpers/mathExpression'

const PRICE_MATH_OPERATOR_KEYS = new Set([
    '+', '-', '*', '/', '(', ')', '=', ' ',
    'Add', 'Subtract', 'Multiply', 'Divide', 'Decimal',
])

function isPriceMathOperatorKey(event) {
    if (PRICE_MATH_OPERATOR_KEYS.has(event.key)) {
        return true
    }

    const code = event.keyCode || event.which

    // Numpad and main keyboard fallbacks (legacy browsers)
    return [107, 109, 106, 111, 110, 187, 189, 191, 173].includes(code)
}

export default {
    methods: {
        isPriceMathField(event) {
            const el = event && event.target
            if (!el) {
                return false
            }

            if (el.dataset && el.dataset.priceMath === '1') {
                return true
            }

            return typeof el.closest === 'function' && el.closest('[data-price-math="1"]') !== null
        },

        allowPriceMathKeydown(event) {
            const allowed = [
                'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                'Home', 'End', '.',
            ]

            if (allowed.includes(event.key)) {
                return
            }

            if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
                return
            }

            if (isPriceMathOperatorKey(event)) {
                return
            }

            if (event.key >= '0' && event.key <= '9') {
                return
            }

            event.preventDefault()
        },

        onPriceMathInput(val, index, field) {
            const sanitized = sanitizePriceMathInput(val)
            this.datasets.rows[index][field] = sanitized
            this.syncPriceMathInputRef(index, field, sanitized)
        },

        onPriceMathModelInput(val, index, field) {
            const sanitized = sanitizePriceMathInput(val)

            if (sanitized !== String(val)) {
                this.datasets.rows[index][field] = sanitized
                this.syncPriceMathInputRef(index, field, sanitized)
            }
        },

        onPriceMathPaste(event, index, field) {
            const pasted = (event.clipboardData || window.clipboardData).getData('text')
            const sanitized = sanitizePriceMathInput(pasted)

            if (sanitized !== pasted) {
                event.preventDefault()
                const input = event.target
                const start = input.selectionStart
                const end = input.selectionEnd
                const value = input.value || ''
                const next = value.slice(0, start) + sanitized + value.slice(end)
                this.datasets.rows[index][field] = next
                this.syncPriceMathInputRef(index, field, next)

                this.$nextTick(() => {
                    const pos = start + sanitized.length
                    input.setSelectionRange(pos, pos)
                })
            }
        },

        priceMathInvalidTitle(field) {
            return field === 'cost' ? 'Invalid Cost' : 'Invalid Price'
        },

        commitPriceMathField(event, index, field, options = {}) {
            const { advanceRow = false, nextCol = 'rate' } = options
            const raw = event && event.target ? event.target.value : (this.datasets.rows[index][field] || '')

            if (!isMathExpression(raw)) {
                if (advanceRow) {
                    this.$nextTick(() => {
                        this.nextRow(index, nextCol)
                    })
                }
                return true
            }

            const result = evaluateMathExpression(raw)

            if (!result.ok) {
                this.showAlertBox('error', this.priceMathInvalidTitle(field), result.error)
                return false
            }

            const formatted = formatPriceMathResult(result.value)
            this.datasets.rows[index][field] = formatted

            if (typeof this.calculateRow === 'function') {
                this.calculateRow(index)
            }

            if (event && event.target) {
                event.target.value = formatted
            }

            this.syncPriceMathInputRef(index, field, formatted)

            if (advanceRow) {
                this.$nextTick(() => {
                    this.nextRow(index, nextCol)
                })
            }

            return true
        },

        syncPriceMathInputRef(index, field, value) {
            const refMap = {
                rate: 'rateinput',
                cost: 'rateinput',
            }
            const refKey = refMap[field]
            if (!refKey) {
                return
            }

            const ref = this.$refs[refKey + index]
            if (!ref) {
                return
            }

            const el = Array.isArray(ref) ? ref[0] : ref
            if (el.localValue !== undefined) {
                el.localValue = value
            }

            const inputEl = el.$el && el.$el.tagName === 'INPUT' ? el.$el : (el.$refs && el.$refs.input) || null
            if (inputEl && inputEl.value !== value) {
                inputEl.value = value
            }
        },

        handlePriceFieldBlur(event, index, field) {
            this.commitPriceMathField(event, index, field, { advanceRow: false })
        },

        handlePriceFieldEnter(event, index, field, nextCol) {
            this.commitPriceMathField(event, index, field, { advanceRow: true, nextCol })
        },
    },
}
